const T = {
  bg: "#06090f", panel: "#0b0f1a", card: "#0f1623", border: "#1a2540",
  gold: "#c9a96e", goldLight: "#e8c98a", goldDim: "#7a6640",
  text: "#e8eaf0", textMid: "#7a8599", textDim: "#3d4a5c",
  active: "#10b981", activeBg: "#071f14", activeText: "#34d399",
  completedBg: "#13132e", completedText: "#a5b4fc", moderate: "#fbbf24",
};

function getAvatarColor(name = "") {
  const palette = ["#1B4F72","#145A32","#6E2F0E","#512E5F","#6B2737","#0E6655","#154360","#784212","#1A5276","#186A3B"];
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  return palette[hash % palette.length];
}

function getInitials(name = "") {
  const clean = name.replace(/^(Dr|Mr|Ms|Mrs)\.?\s*/i, "");
  const parts = clean.trim().split(/\s+/);
  return parts.slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";
}

// ── PURE SVG DONUT CHART ──────────────────────────────────────────────────────
function DonutChart({ segments, size = 160, thickness = 28 }) {
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const total = segments.reduce((s, seg) => s + seg.value, 0);

  let offset = 0;
  const arcs = segments.map((seg) => {
    const pct = total > 0 ? seg.value / total : 0;
    const dash = pct * circumference;
    const gap = circumference - dash;
    const arc = { ...seg, dash, gap, offset: offset * circumference };
    offset += pct;
    return arc;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background circle */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={T.border} strokeWidth={thickness} />
      {arcs.map((arc, i) => (
        arc.value > 0 && (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={arc.color}
            strokeWidth={thickness - 3}
            strokeDasharray={`${arc.dash} ${arc.gap}`}
            strokeDashoffset={-arc.offset + circumference * 0.25}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 0.5s ease" }}
          />
        )
      ))}
    </svg>
  );
}

// ── HORIZONTAL BAR ────────────────────────────────────────────────────────────
function HBar({ label, value, max, color, count }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
        <span style={{ color: T.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{label}</span>
        <span style={{ fontWeight: 700, color, flexShrink: 0 }}>{count} <span style={{ color: T.textDim, fontWeight: 400 }}>({Math.round(pct)}%)</span></span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: T.border, overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 4, background: color, width: `${pct}%`, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "20px 22px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color }} />
      <div style={{ fontSize: 10, color: T.textDim, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 10 }}>{label}</div>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 34, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: T.textDim, marginTop: 6 }}>{sub}</div>
    </div>
  );
}

export default function Analytics({ patients, therapists, stats }) {
  const conditionCounts = Object.entries(
    patients.reduce((acc, p) => {
      if (p.condition) acc[p.condition] = (acc[p.condition] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]).slice(0, 8);

  const severe = patients.filter(p => p.painScore >= 7).length;
  const moderate = patients.filter(p => p.painScore >= 4 && p.painScore < 7).length;
  const mild = patients.filter(p => p.painScore < 4).length;
  const paid = patients.filter(p => p.payments === "Paid").length;
  const pending = patients.filter(p => p.payments === "Pending").length;
  const partial = patients.filter(p => p.payments === "Partial").length;
  const avgPain = patients.length ? (patients.reduce((s, p) => s + (p.painScore || 0), 0) / patients.length).toFixed(1) : "—";
  const recoveryRate = patients.length ? Math.round((stats.completed / patients.length) * 100) : 0;
  const pendingRevenue = patients.filter(p => p.payments !== "Paid").reduce((s, p) => s + (p.amount || 0), 0);

  const CONDITION_COLORS = [
    T.gold, T.activeText, "#a5b4fc", T.moderate,
    "#22d3ee", "#f87171", "#fb923c", "#a78bfa",
  ];

  return (
    <div style={{ padding: "30px 16px" }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 600, marginBottom: 4 }}>Analytics</div>
        <div style={{ color: T.textMid, fontSize: 13 }}>
          Clinic performance overview · {patients.length} patients · {therapists.length} therapist{therapists.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 28 }}>
        <StatCard label="Total Patients" value={stats.total} sub="All registrations" color={T.gold} />
        <StatCard label="Active Cases" value={stats.active} sub={`${Math.round((stats.active/Math.max(stats.total,1))*100)}% of total`} color={T.activeText} />
        <StatCard label="Recovery Rate" value={`${recoveryRate}%`} sub={`${stats.completed} discharged`} color="#818cf8" />
        <StatCard label="Total Revenue" value={`₹${stats.revenue.toLocaleString()}`} sub={`₹${Math.round(stats.revenue/Math.max(stats.total,1)).toLocaleString()} avg/patient`} color="#a78bfa" />
      </div>

      {/* Row 1: Conditions + Pain Distribution */}
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20, marginBottom: 20 }}>
        {/* Conditions */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 26 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim, marginBottom: 18 }}>Conditions Treated</div>
          {patients.length === 0
            ? <div style={{ color: T.textDim, fontSize: 13, textAlign: "center", padding: 40 }}>No patient data yet.</div>
            : conditionCounts.map(([cond, count], i) => (
              <HBar
                key={cond}
                label={cond}
                value={count}
                max={conditionCounts[0][1]}
                color={CONDITION_COLORS[i % CONDITION_COLORS.length]}
                count={count}
              />
            ))
          }
        </div>

        {/* Pain Distribution */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 26 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim, marginBottom: 18 }}>Pain Score Distribution</div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 20, position: "relative" }}>
            <DonutChart
              size={160}
              thickness={28}
              segments={[
                { value: severe, color: "#f87171", label: "Severe" },
                { value: moderate, color: T.moderate, label: "Moderate" },
                { value: mild, color: T.activeText, label: "Mild" },
              ]}
            />
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center" }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 700, color: T.gold }}>{avgPain}</div>
              <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.5px" }}>Avg NPRS</div>
            </div>
          </div>
          {[
            { label: "Severe (7–10)", count: severe, color: "#f87171" },
            { label: "Moderate (4–6)", count: moderate, color: T.moderate },
            { label: "Mild (0–3)", count: mild, color: T.activeText },
          ].map((r) => (
            <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: T.textMid }}>{r.label}</span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: r.color }}>{r.count} <span style={{ fontSize: 11, color: T.textDim, fontWeight: 400 }}>patients</span></span>
            </div>
          ))}
        </div>
      </div>

      {/* Row 2: Payment + Status + Today */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 20 }}>
        {/* Payment */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 26 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim, marginBottom: 18 }}>Payment Status</div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, position: "relative" }}>
            <DonutChart
              size={140}
              thickness={26}
              segments={[
                { value: paid, color: T.activeText },
                { value: pending, color: T.moderate },
                { value: partial, color: "#a78bfa" },
              ]}
            />
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center" }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: T.gold }}>{patients.length}</div>
              <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase" }}>Total</div>
            </div>
          </div>
          {[
            { label: "Paid", count: paid, color: T.activeText },
            { label: "Pending", count: pending, color: T.moderate },
            { label: "Partial", count: partial, color: "#a78bfa" },
          ].map((r) => (
            <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: r.color }} />
                <span style={{ fontSize: 12, color: T.textMid }}>{r.label}</span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: r.color }}>{r.count}</span>
            </div>
          ))}
          <div style={{ marginTop: 14, background: T.panel, borderRadius: 10, padding: 14, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 4 }}>Total Revenue</div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 700, color: T.gold }}>₹{stats.revenue.toLocaleString()}</div>
            <div style={{ fontSize: 11, color: "#f87171", marginTop: 3 }}>Pending: ₹{pendingRevenue.toLocaleString()}</div>
          </div>
        </div>

        {/* Patient Status */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 26 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim, marginBottom: 18 }}>Patient Status</div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, position: "relative" }}>
            <DonutChart
              size={140}
              thickness={26}
              segments={[
                { value: stats.active, color: T.activeText },
                { value: stats.completed, color: "#a5b4fc" },
              ]}
            />
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center" }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: T.gold }}>{recoveryRate}%</div>
              <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase" }}>Recovery</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
            <div style={{ background: T.activeBg, borderRadius: 9, padding: 14, textAlign: "center", border: `1px solid ${T.activeText}22` }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 700, color: T.activeText }}>{stats.active}</div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 2, textTransform: "uppercase" }}>Active</div>
            </div>
            <div style={{ background: T.completedBg, borderRadius: 9, padding: 14, textAlign: "center", border: `1px solid ${T.completedText}22` }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 700, color: T.completedText }}>{stats.completed}</div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 2, textTransform: "uppercase" }}>Completed</div>
            </div>
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textDim, marginBottom: 5 }}>
              <span>Overall Recovery Rate</span>
              <span style={{ color: T.activeText, fontWeight: 700 }}>{recoveryRate}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: T.border, overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 3, background: `linear-gradient(90deg,${T.active},${T.activeText})`, width: `${recoveryRate}%`, transition: "width 0.5s" }} />
            </div>
          </div>
        </div>

        {/* Today's Overview */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 26 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim, marginBottom: 18 }}>Today's Overview</div>
          <div style={{ display: "grid", gap: 10 }}>
            {[
              { label: "Today's Appointments", val: stats.todayAppts, col: "#22d3ee", icon: "◷" },
              { label: "Pending Payments", val: stats.pendingPay, col: T.moderate, icon: "₹" },
              { label: "Avg Pain Score", val: `${avgPain}/10`, col: T.gold, icon: "◉" },
              { label: "Active Therapists", val: therapists.length, col: T.activeText, icon: "✚" },
            ].map((s) => (
              <div key={s.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", background: T.panel, borderRadius: 9, border: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 16, color: s.col }}>{s.icon}</span>
                  <span style={{ fontSize: 12, color: T.textMid }}>{s.label}</span>
                </div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: s.col }}>{s.val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Therapist Summary */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 26 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim, marginBottom: 18 }}>Therapist Performance</div>
        {therapists.length === 0
          ? <div style={{ color: T.textDim, fontSize: 13, textAlign: "center", padding: 24 }}>No therapists added yet.</div>
          : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
              {therapists.map((t) => {
                const tPatients = patients.filter(p => p.therapist === t.name);
                const revenue = tPatients.reduce((s, p) => s + (p.amount || 0), 0);
                const total = tPatients.length;
                const active = tPatients.filter(p => p.status === "Active").length;
                const completed = tPatients.filter(p => p.status === "Completed").length;
                const recovRate = total ? Math.round((completed / total) * 100) : 0;
                const tAvgPain = total ? (tPatients.reduce((s, p) => s + (p.painScore || 0), 0) / total).toFixed(1) : "—";

                return (
                  <div key={t.id} style={{ background: T.panel, borderRadius: 12, padding: 20, border: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                      <div style={{ width: 46, height: 46, borderRadius: "50%", background: getAvatarColor(t.name), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                        {t.avatar || getInitials(t.name)}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{t.name}</div>
                        <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>{t.specialty || "Physiotherapist"}</div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                      {[
                        ["Patients", total, T.gold],
                        ["Active", active, T.activeText],
                        ["Revenue", `₹${revenue.toLocaleString()}`, "#a78bfa"],
                        ["Recovery", `${recovRate}%`, "#22d3ee"],
                      ].map(([label, val, col]) => (
                        <div key={label} style={{ background: T.card, borderRadius: 8, padding: "10px 12px" }}>
                          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 700, color: col }}>{val}</div>
                          <div style={{ fontSize: 9, color: T.textDim, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: T.card, borderRadius: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 11, color: T.textDim }}>Avg patient pain</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: Number(tAvgPain) <= 3 ? T.activeText : Number(tAvgPain) <= 6 ? T.moderate : "#f87171" }}>{tAvgPain}/10</span>
                    </div>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textDim, marginBottom: 5 }}>
                        <span>Caseload activity</span>
                        <span style={{ color: T.gold, fontWeight: 700 }}>{total ? Math.round((active/total)*100) : 0}%</span>
                      </div>
                      <div style={{ height: 5, borderRadius: 3, background: T.border, overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 3, background: `linear-gradient(90deg,${T.gold},${T.goldLight})`, width: `${total ? Math.min((active/total)*100,100) : 0}%`, transition: "width 0.5s" }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        }
      </div>
    </div>
  );
}